import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SuperAdminSidebar from "../components/SuperAdminSidebar";
import api from "../lib/api";
import { Store, CheckCircle2, CalendarDays, Clock, Plus, ExternalLink, Euro } from "lucide-react";

export default function SuperAdminDashboard() {
    const [stats, setStats] = useState(null);
    const [restaurants, setRestaurants] = useState([]);
    const [revenue, setRevenue] = useState(null);

    useEffect(() => {
        api.get("/superadmin/stats").then((r) => setStats(r.data)).catch(() => {});
        api.get("/superadmin/restaurants").then((r) => setRestaurants(r.data)).catch(() => {});
        api.get("/superadmin/revenue").then((r) => setRevenue(r.data)).catch(() => {});
    }, []);

    return (
        <div className="flex min-h-screen bg-ink" data-testid="superadmin-dashboard">
            <SuperAdminSidebar />
            <main className="flex-1 p-10">
                <div className="mb-10 flex items-start justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-gold text-xs tracking-[0.4em] uppercase">Renova · Super Admin</div>
                        <h1 className="font-serif text-5xl text-white mt-2">Panoramica</h1>
                    </div>
                    <Link to="/adminrenova/restaurants/new" data-testid="new-restaurant-btn"
                        className="inline-flex items-center gap-2 bg-gold text-black font-semibold px-5 py-3 rounded-sm hover:bg-gold-light transition-colors">
                        <Plus size={16} /> Nuovo ristorante
                    </Link>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                    <StatCard icon={Store} label="Ristoranti totali" value={stats?.restaurants_total ?? "—"} />
                    <StatCard icon={CheckCircle2} label="Attivi" value={stats?.restaurants_active ?? "—"} accent />
                    <StatCard icon={CalendarDays} label="Prenotazioni totali" value={stats?.reservations_total ?? "—"} />
                    <StatCard icon={Clock} label="Oggi (tutti)" value={stats?.reservations_today ?? "—"} />
                </div>

                {/* Revenue Renova */}
                <div className="bg-gradient-to-br from-gold/10 to-gold/5 border border-gold/30 rounded-sm p-8 mb-10" data-testid="revenue-panel">
                    <div className="flex items-start justify-between flex-wrap gap-4">
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <Euro size={16} className="text-gold" strokeWidth={1.5} />
                                <span className="text-gold text-xs tracking-[0.4em] uppercase">Ricavi Renova</span>
                            </div>
                            <div className="font-serif text-6xl text-white" data-testid="total-commission">
                                € {((revenue?.total_commission_cents || 0) / 100).toFixed(2)}
                            </div>
                            <div className="text-sm text-gray-400 mt-2">
                                Commissione 1€ × coperto · {revenue?.total_covers || 0} coperti totali · {revenue?.total_reservations || 0} prenotazioni
                            </div>
                        </div>
                    </div>
                    {revenue?.per_restaurant?.length > 0 && (
                        <div className="mt-6 pt-6 border-t border-gold/20">
                            <div className="text-[10px] uppercase tracking-[0.3em] text-gray-400 font-semibold mb-4">Top ristoranti</div>
                            <div className="space-y-2">
                                {revenue.per_restaurant.slice(0, 5).map((r) => (
                                    <div key={r.restaurant_id} className="flex items-center justify-between py-2 text-sm" data-testid={`revenue-${r.restaurant_slug}`}>
                                        <span className="text-white">{r.restaurant_name}</span>
                                        <div className="flex items-center gap-6 text-xs text-gray-400">
                                            <span>{r.reservations} prenot.</span>
                                            <span>{r.covers} coperti</span>
                                            <span className="text-gold font-semibold text-sm">€ {(r.commission_cents / 100).toFixed(2)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-ink-surface border border-white/10 rounded-sm p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="font-serif text-2xl text-white">Ristoratori attivi</h2>
                        <Link to="/adminrenova/restaurants" className="text-xs text-gold tracking-widest uppercase hover:text-gold-light">
                            Gestisci tutti →
                        </Link>
                    </div>
                    {restaurants.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-gray-400 font-serif text-xl mb-4">Nessun ristorante ancora.</p>
                            <Link to="/adminrenova/restaurants/new" className="inline-block bg-gold text-black px-6 py-3 rounded-sm font-semibold">
                                Crea il primo
                            </Link>
                        </div>
                    ) : (
                        <div className="divide-y divide-white/5">
                            {restaurants.slice(0, 8).map((r) => (
                                <div key={r.restaurant_id} className="py-4 flex items-center justify-between gap-4" data-testid={`rest-row-${r.slug}`}>
                                    <div>
                                        <div className="flex items-center gap-3">
                                            <span className="font-serif text-xl text-white">{r.name}</span>
                                            {r.active
                                                ? <span className="text-[9px] uppercase tracking-widest bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 px-2 py-0.5 rounded-sm">Attivo</span>
                                                : <span className="text-[9px] uppercase tracking-widest bg-red-500/10 text-red-400 border border-red-500/30 px-2 py-0.5 rounded-sm">Disattivato</span>}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                            /r/{r.slug} · Owner: {r.owner_username} · {r.reservations_count} prenotazioni
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <a href={`/r/${r.slug}`} target="_blank" rel="noreferrer"
                                            className="flex items-center gap-1 text-xs text-gray-400 hover:text-gold px-3 py-2 border border-white/10 rounded-sm"
                                            data-testid={`visit-${r.slug}`}>
                                            <ExternalLink size={12} /> Visita
                                        </a>
                                        <Link to={`/adminrenova/restaurants/${r.restaurant_id}`}
                                            className="text-xs text-gold px-3 py-2 border border-gold/40 rounded-sm hover:bg-gold/10"
                                            data-testid={`manage-${r.slug}`}>
                                            Gestisci
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}

function StatCard({ icon: Icon, label, value, accent }) {
    return (
        <div className={`p-6 rounded-sm border ${accent ? "border-gold/40 bg-gold/5" : "border-white/10 bg-ink-surface"}`}>
            <Icon size={20} className={accent ? "text-gold" : "text-gray-500"} strokeWidth={1.5} />
            <div className="text-xs text-gray-500 tracking-widest uppercase mt-4">{label}</div>
            <div className="font-serif text-4xl text-white mt-1">{value}</div>
        </div>
    );
}
